import { useEffect, useState } from "react";
import {
  effectivenessList,
  EffectivenessType,
  informationCollectionToolList,
  InformationCollectionToolType,
} from "../../utils/types";
import { useRequestStore } from "../../data/stores/loggerStore";
import { apiCall } from "../../data/useFetcher";

type CategoryData = {
  id: string;
  name: string;
  color: string;
};
type SystemsData = {
  id: string;
  label: string;
  type: InformationCollectionToolType;
  effectiveness: EffectivenessType;
  categories: string[];
};
type KpisData = {
  id: string;
  label: string;
  systemIds: string[];
};
type CollectionsData = {
  id: string;
  label: string;
  systemIds: string[];
  categories: string[];
  color?: string;
};

export const categories: CategoryData[] = [
  { id: "it", name: "IT", color: "bg-blue-200" },
  { id: "legal", name: "Legal", color: "bg-red-200" },
  { id: "procurement", name: "Procurement", color: "bg-yellow-200" },
  { id: "hr", name: "HR", color: "bg-purple-200" },
  { id: "finance", name: "Finance", color: "bg-green-200" },
  { id: "commercial", name: "Commercial", color: "bg-orange-200" },
  { id: "engineering", name: "Engineering", color: "bg-pink-200" },
];

export const systems: SystemsData[] = [
  {
    id: "governance",
    label: "Governance",
    categories: ["legal", "engineering"],
    effectiveness: effectivenessList?.[0],
    type: informationCollectionToolList?.[0],
  },
  {
    id: "data-mgmt",
    label: "Data Management",
    categories: ["it"],
    effectiveness: effectivenessList?.[2],
    type: informationCollectionToolList?.[2],
  },
  {
    id: "compliance",
    label: "Compliance",
    categories: ["legal"],
    effectiveness: effectivenessList?.[1],
    type: informationCollectionToolList?.[3],
  },
  {
    id: "reporting",
    label: "Reporting",
    categories: ["finance"],
    effectiveness: effectivenessList?.[3],
    type: informationCollectionToolList?.[4],
  },
  {
    id: "supply-chain",
    label: "Supply Chain",
    categories: ["procurement"],
    effectiveness: effectivenessList?.[2],
    type: informationCollectionToolList?.[5],
  },
  {
    id: "hiring",
    label: "Hiring",
    categories: ["hr"],
    effectiveness: effectivenessList?.[1],
    type: informationCollectionToolList?.[6],
  },
  {
    id: "site-design",
    label: "Site Design",
    categories: ["engineering"],
    effectiveness: effectivenessList?.[0],
    type: informationCollectionToolList?.[7],
  },
  {
    id: "sales-platform",
    label: "Sales Platform",
    categories: ["commercial"],
    effectiveness: effectivenessList?.[3],
    type: informationCollectionToolList?.[8],
  },
  {
    id: "admin",
    label: "Administration",
    categories: ["hr", "procurement"],
    effectiveness: effectivenessList?.[2],
    type: informationCollectionToolList?.[0],
  },
];

export const kpis: KpisData[] = [
  {
    id: "risk-mgmt",
    label: "Risk Management",
    systemIds: ["governance", "compliance"],
  },
  {
    id: "data-quality",
    label: "Data Quality",
    systemIds: ["data-mgmt", "compliance", "governance"],
  },
  {
    id: "recruitment-effectiveness",
    label: "Recruitment Effectiveness",
    systemIds: ["hiring", "data-mgmt"],
  },
  {
    id: "supply-cost",
    label: "Supply Cost",
    systemIds: ["supply-chain", "hiring"],
  },
  {
    id: "report-accuracy",
    label: "Report Accuracy",
    systemIds: ["reporting", "admin", "supply-chain"],
  },
];

export const collections: CollectionsData[] = [
  {
    id: "collection-alpha",
    label: "Collection Alpha",
    categories: ["procurement"], // connected categories by id
    color: "#8b5cf6", // purple shade
    systemIds: ["governance"], // connected systems by id
  },
  {
    id: "collection-beta",
    label: "Collection Beta",
    categories: ["engineering"],
    color: "#10b981", // emerald green
    systemIds: ["supply-chain"],
  },
  {
    id: "collection-gamma",
    label: "Collection Gamma",
    categories: ["it"],
    color: "#f59e0b", // amber
    systemIds: ["sales-platform"],
  },
];

export const useFetchSystem = ({ organization }: { organization?: string }) => {
  const [data, setData] = useState<{
      categories: CategoryData[];
      systems: SystemsData[];
      collections: CollectionsData[];
      kpis: KpisData[];
    }>({ categories: [], systems: [], collections: [], kpis: [] }),
    { getDynamicLogger, dataSystemAll }: any = useRequestStore();

  useEffect(() => {
    getDynamicLogger({}, "dataSystemAll");
    apiCall({
      type: "get",
      url: `/api/v1/data-system${
        organization ? `?organization=${organization}` : ""
      }`,
      getter: (d: any) => getDynamicLogger(d, "dataSystemAll"),
      noToast: true,
    });
  }, [organization]);

  useEffect(() => {
    if (dataSystemAll?.docs) {
      let newCategories: CategoryData[] = [],
        newSystems: SystemsData[] = [],
        newCollections: CollectionsData[] = [],
        newKpis: KpisData[] = [];
      // console.log({ dataIn: dataSystemAll });

      for (let d = 0; d < dataSystemAll?.docs.length; d++) {
        const element = dataSystemAll?.docs[d],
          departmentName = element?.profile?.user?.departmentName
            ?.toLowerCase()
            ?.replace(/\s+/g, "")
            ?.trim();
        if (element?.profile) {
          let findCategories = newCategories?.find(
            (it) => it?.id === departmentName
          );
          if (!findCategories) {
            newCategories.push({
              id: departmentName,
              name: element?.profile?.user?.departmentName,
              color:
                categories?.[newCategories?.length]?.color || "bg-gray-200",
            });
          }
          let findSystems = newSystems?.find((it) => it?.id === element?._id);
          if (!findSystems) {
            newSystems.push({
              id: element?._id,
              label: element?.erpModule,
              categories: [departmentName],
              effectiveness: Array.isArray(element?.effectivenessPurposeLevel)
                ? element?.effectivenessPurposeLevel?.[0]
                : element?.effectivenessPurposeLevel,
              type: Array?.isArray(element?.informationCollectionTool)
                ? element?.informationCollectionTool?.[0]
                : element?.informationCollectionTool,
            });
          }
          let findCollections = newCollections?.find(
            (it) =>
              it?.id ===
              element?.toolUsedToCollectData
                ?.toLowerCase()
                ?.replace(/\s+/g, "")
                ?.trim()
          );
          if (!findCollections) {
            newCollections.push({
              id: element?.toolUsedToCollectData
                ?.toLowerCase()
                ?.replace(/\s+/g, "")
                ?.trim(),
              label: element?.toolUsedToCollectData,
              categories: [departmentName],
              systemIds: [element?._id],
            });
          } else {
            let findCateg = findCollections?.categories?.find(
              (it) => it === departmentName
            );
            if (!findCateg) {
              findCollections?.categories?.push(departmentName);
            }
            findCollections?.systemIds?.push(element?._id);
            newCollections = newCollections?.map((it) => {
              if (it?.id === findCollections?.id) {
                return findCollections;
              }
              return it;
            });
          }
          if (
            element?.systemInformKPI &&
            Array.isArray(element?.systemInformKPI)
          ) {
            for (let k = 0; k < element?.systemInformKPI?.length; k++) {
              const kpi = element?.systemInformKPI[k];
              let findKpis = newKpis?.find(
                (it) =>
                  it?.id === kpi?.toLowerCase()?.replace(/\s+/g, "")?.trim()
              );
              if (!findKpis) {
                newKpis.push({
                  id: kpi?.toLowerCase()?.replace(/\s+/g, "")?.trim(),
                  label: kpi,
                  systemIds: [element?._id],
                });
              } else {
                findKpis?.systemIds?.push(element?._id);
                newKpis = newKpis?.map((it) => {
                  if (it?.id === findKpis?.id) {
                    return findKpis;
                  }
                  return it;
                });
              }
            }
          }
        }
      }
      setData({
        categories: newCategories,
        systems: newSystems,
        collections: newCollections,
        kpis: newKpis,
      });
    }
  }, [dataSystemAll]);
  // console.table({ data });
  return { ...data };
};
